import type { ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/cn";

/**
 * Pill controls, matching the public site's header buttons. Each variant walks its own ramp:
 * rest → hover → active are separate steps, never one accent value reused.
 */
const variants = {
  primary:
    "bg-accent text-accent-ink font-semibold hover:bg-accent-strong active:bg-accent-deep active:scale-[0.98]",
  secondary:
    "border border-line bg-surface text-ink hover:border-line-strong hover:bg-raised active:bg-well",
  quiet:
    "border border-accent-line bg-accent-bg text-accent-text hover:border-accent-line-hover hover:bg-accent-bg-hover active:bg-accent-bg",
  ghost: "text-muted hover:bg-raised hover:text-ink active:bg-well",
  danger:
    "border border-danger-line bg-danger-bg text-danger-text hover:border-danger hover:bg-danger/15 active:bg-danger-bg",
} as const;

const sizes = {
  sm: "h-7 gap-1.5 px-2.5 text-caption",
  md: "h-8 gap-2 px-3.5 text-ui",
  lg: "h-9 gap-2 px-4 text-ui-lg",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
}

export function Button({
  variant = "secondary",
  size = "md",
  className,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "ease-out-expo inline-flex shrink-0 items-center justify-center rounded-full whitespace-nowrap transition-[background-color,border-color,color,transform] duration-200 disabled:pointer-events-none disabled:opacity-45",
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  );
}
